import type { UploadResponse } from '@kanbu/schema/contracts';

import { trpcClient } from '@/services/trpc';

import type { FileUploadAdapter } from '../fileUploadTypes';

type QueueOperation =
  | {
      type: 'upload';
      file: File;
    }
  | {
      type: 'delete';
      file: string | File; // Either File object or public URL
    };

export interface ScopedLazyFileUploadAdapter extends FileUploadAdapter {
  commit: (
    relativePath?: string,
  ) => Promise<UploadResponse[] | { publicUrl: string | null }[]>;
}

export interface LazyFileUploadManager {
  getAdapter: (id: string) => ScopedLazyFileUploadAdapter;
  commitAll: (options?: {
    relativePath?: string;
  }) => Promise<
    Record<string, (UploadResponse | { publicUrl: string | null })[]>
  >;
}

/**
 * This is a manager for lazy file upload adapters.
 *
 * It enables us to separate upload operation from the commit operation.
 *
 * This is useful because we can upload files to the storage in parallel
 * and then commit them in order.
 *
 * This is also useful because we can use the same adapter for multiple
 * upload operations.
 */
export const createLazyFileUploadManager = (): LazyFileUploadManager => {
  const queues = new Map<string, QueueOperation[]>();

  const commit = async (
    queue: QueueOperation[],
    relativePath?: string,
  ): Promise<(UploadResponse | { publicUrl: string | null })[]> => {
    const results = [];

    if (queue.length === 0) {
      return [];
    }

    /**
     * Process the queue in order.
     *
     * We are not using Promise.all here because we want to process the queue
     * in order.
     */
    for (const operation of queue) {
      if (operation.type === 'upload') {
        const response = await trpcClient.upload.upload.mutate({
          relativePath,
          fileName: operation.file.name.trim(),
          contentType: operation.file.type,
        });

        const uploadResponse = await fetch(response.uploadUrl, {
          method: 'PUT',
          body: operation.file,
          mode: 'cors',
          headers: {
            'Content-Type': operation.file.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload file to storage');
        }

        results.push(response);
      } else if (operation.type === 'delete') {
        await trpcClient.upload.delete.mutate({
          publicUrl:
            typeof operation.file === 'string'
              ? operation.file
              : operation.file.name,
        });

        results.push({ publicUrl: '' });
      }
    }

    // Clear the queue after processing
    queue.length = 0;

    return results;
  };

  return {
    /**
     * Use this to get specific adapter scoped to a given id.
     *
     * This is usefull if you for example need adapter to upload logo,
     * avatar, system image, each one gets separate adapter. So you
     * can later identify which file was uploaded to which adapter
     * and save it to the database correctly.
     */
    getAdapter: (id: string) => {
      if (!queues.has(id)) {
        queues.set(id, []);
      }

      const queue = queues.get(id)!;

      return {
        upload: async (file: File) => {
          queue.push({ type: 'upload', file });
        },
        delete: async (fileOrUrl: File | string) => {
          if (typeof fileOrUrl !== 'string') {
            const uploadIndex = queue.findIndex(
              q => q.type === 'upload' && q.file === fileOrUrl,
            );

            if (uploadIndex !== -1) {
              queue.splice(uploadIndex, 1);

              return;
            }
          }

          queue.push({ type: 'delete', file: fileOrUrl });
        },
        commit: relativePath => {
          return commit(queue, relativePath);
        },
      };
    },

    /**
     * Helper for committing all files in the queue at once.
     */
    commitAll: async options => {
      const allResults: Record<
        string,
        (UploadResponse | { publicUrl: string | null })[]
      > = {};

      for (const [id, queue] of queues.entries()) {
        allResults[id] = await commit(queue, options?.relativePath);
      }

      return allResults;
    },
  };
};
